<template>
  <div
    class="SearchBar"
    :class="[attrs.class, { 'SearchBar--sm': isMobile, 'is-filled': isFilled }]"
    :style="attrs.style"
  >
    <!-- Results follow the model as the user types, so submitting has nothing left to do. The
         default is suppressed to stay on the page, for the button and for Enter alike. -->
    <form class="SearchBar-form" role="search" @submit.prevent>
      <input
        :id
        ref="inputRef"
        v-model="searchTerm"
        class="SearchBar-input"
        type="search"
        :name
        :placeholder
        :aria-label="ariaLabel"
        v-bind="inputAttrs"
      >

      <div class="SearchBar-actions">
        <ActionButton
          v-if="isFilled"
          type="button"
          variant="tertiary"
          :size="buttonSize"
          leftIcon="window-close"
          :aria-label="clearAriaLabel"
          @click="onClear"
        />

        <ActionButton
          v-if="isSupported"
          class="SearchBar-micButton"
          :class="{ 'is-listening': isListening }"
          type="button"
          variant="tertiary"
          :size="buttonSize"
          leftIcon="microphone"
          :aria-label="micAriaLabel"
          :aria-pressed="isListening"
          @click="toggle()"
        />

        <ActionButton
          type="submit"
          flipRtl
          :size="buttonSize"
          rightIcon="arrow-right"
          :aria-label="submitAriaLabel"
        />
      </div>
    </form>

    <p v-if="hasMicError && micErrorMessage" class="SearchBar-error" role="alert">
      {{ micErrorMessage }}
    </p>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, useAttrs, useTemplateRef, watch } from 'vue';
import { useI18n } from '@cvo/i18n/composables/useI18n';
import { useSpeechRecognition } from '@vueuse/core';
import ActionButton from '@cvo/ui/components/atoms/ActionButton/ActionButton.vue';
import { useResponsive } from '@cvo/ui/composables/useResponsive';
import type { SearchBarProps, SearchBarEmits } from './SearchBar.model';

defineProps<SearchBarProps>();
const emit = defineEmits<SearchBarEmits>();

// Attributes land on the input rather than on the wrapper, so consumers can pass native input
// attributes straight through. Class and style stay on the root, where the layout expects them.
defineOptions({ inheritAttrs: false });

const attrs = useAttrs();

const inputAttrs = computed(() => {
  const { class: _class, style: _style, ...rest } = attrs;
  return rest;
});

const searchTerm = defineModel<string>({ default: '' });

const { locale } = useI18n();
const { isMobile } = useResponsive();

const buttonSize = computed(() => (isMobile.value ? 'sm' : 'md'));
const isFilled = computed(() => searchTerm.value.length > 0);

const inputRef = useTemplateRef('inputRef');

const { isSupported, isListening, result, error, toggle, stop } = useSpeechRecognition({
  lang: () => locale.value,
  continuous: false,
  interimResults: true,
});

const hasMicError = ref(false);

// Interim transcripts land in the model too, so results filter while the user is still speaking.
// Only non-empty values are written, so a reset transcript cannot wipe a typed query. Gated on
// the listening state as well: stopping drops that flag synchronously, while the engine still
// delivers one last transcript afterwards, which would otherwise refill a cleared query.
// Known limitation: the transcript is never reset, so repeating a phrase verbatim after a clear
// produces the same value and writes nothing.
watch(result, (transcript) => {
  if (transcript && isListening.value) searchTerm.value = transcript;
});

// A denied or failed permission must not leave the button stuck in its listening state, and it is
// otherwise invisible: the mic simply never picks anything up.
watch(error, (event) => {
  if (!event) return;
  console.error('Speech recognition failed', event);
  emit('error', event);
  hasMicError.value = true;
  stop();
});

// A new attempt clears the previous failure.
watch(isListening, (listening) => {
  if (listening) hasMicError.value = false;
});

function onClear() {
  // Clearing ends the dictation, otherwise the query the user just wiped comes straight back.
  stop();
  searchTerm.value = '';
  inputRef.value?.focus();
}
</script>

<style lang="scss">
  @use '@cvo/ui/styles/design-system' as vars;
  @use '@cvo/ui/styles/utils/text';

  @layer cvo-ui {
    .SearchBar {
      display: flex;
      flex-direction: column;
      gap: vars.$dimensions-d-2;
      width: 100%;

      // The bar itself. The root only stacks it above the error message.
      &-form {
        display: flex;
        align-items: center;
        padding: vars.$dimensions-d-4;
        background-color: vars.$molecules-searchbar-bg-color;
        border-radius: vars.$border-radius-lg;
        box-shadow: vars.$shadow-md;

        // Replaces the suppressed native input outline.
        &:focus-within {
          outline: vars.$dimensions-d-0-5 solid vars.$color-brand-primary-900;
          outline-offset: vars.$dimensions-d-0-5;
        }
      }

      &-error {
        color: vars.$color-system-status-danger-900;
        padding-inline: vars.$dimensions-d-4;
        @include text.text('sm');
      }

      &-input {
        flex: 1;
        min-width: 0;
        padding-block: 0;
        padding-inline: vars.$dimensions-d-3;
        border: none;
        outline: none;
        background: transparent;
        color: vars.$molecules-searchbar-text-color;
        text-overflow: ellipsis;
        @include text.text('md');

        &::placeholder {
          color: vars.$molecules-searchbar-placeholder-color;
        }

        // The molecule renders its own clear button.
        &::-webkit-search-cancel-button {
          appearance: none;
        }
      }

      &-actions {
        display: flex;
        gap: vars.$dimensions-d-1;

        // The atom's per-size min-widths are meant for labelled buttons; these are icon-only.
        .Button {
          min-width: 0;
        }
      }

      // Figma specifies no listening state, so the mic borrows the submit button's brand colour
      // while it is live. The tertiary variant declares its text colour dynamic, so this is the
      // atom's own recolouring hook rather than a cascade fight with it.
      &-micButton.is-listening {
        --button-color: #{vars.$color-brand-primary-900};
      }

      &--sm {
        .SearchBar-form {
          padding-block: vars.$dimensions-d-2;
          padding-inline: vars.$dimensions-d-3 vars.$dimensions-d-2;
          border-radius: vars.$border-radius-md;
        }

        .SearchBar-input {
          padding-inline: 0;
          @include text.text('sm');
        }

        .SearchBar-error {
          padding-inline: vars.$dimensions-d-3;
        }
      }
    }
  }
</style>
